import { RefObject, useLayoutEffect, useRef, useState } from 'react';

const NO_OFFSETS: Record<string, number> = {};

interface UsePinnedOffsetsOptions {
  // Both lists must keep their identity between renders unless the pins change.
  endFields: string[];
  leadingColumnsWidth: number;
  startFields: string[];
  tableRef: RefObject<HTMLTableElement | null>;
}

const haveSameOffsets = (
  previous: Record<string, number>,
  next: Record<string, number>,
) =>
  Object.keys(previous).length === Object.keys(next).length &&
  Object.entries(next).every(([field, offset]) => previous[field] === offset);

// A pinned column sticks after the pinned columns before it, so its offset is their
// combined width. Their widths come from automatic table layout, so a side with more
// than one pin is measured; a lone pin needs no measuring.
export default function usePinnedOffsets({
  endFields,
  leadingColumnsWidth,
  startFields,
  tableRef,
}: UsePinnedOffsetsOptions) {
  const [offsets, setOffsets] = useState(NO_OFFSETS);
  const offsetsRef = useRef(NO_OFFSETS);

  useLayoutEffect(() => {
    const table = tableRef.current;
    const needsMeasuring = startFields.length > 1 || endFields.length > 1;

    if (!table || !needsMeasuring) {
      offsetsRef.current = NO_OFFSETS;
      setOffsets(NO_OFFSETS);

      return;
    }

    const headerCells = new Map(
      Array.from(table.querySelectorAll<HTMLElement>('th[data-field]'))
        .filter((cell) => cell.closest('table') === table)
        .map((cell) => [cell.dataset.field, cell]),
    );
    // Automatic layout gives columns fractional widths; whole-pixel offsets would leave
    // a hairline gap or overlap between stacked sticky columns.
    const widthOf = (field: string) =>
      headerCells.get(field)?.getBoundingClientRect().width ?? 0;

    const measure = () => {
      const nextOffsets: Record<string, number> = {};
      let left = leadingColumnsWidth;
      let right = 0;

      startFields.forEach((field) => {
        nextOffsets[field] = left;
        left += widthOf(field);
      });
      [...endFields].reverse().forEach((field) => {
        nextOffsets[field] = right;
        right += widthOf(field);
      });

      // Even an updater that returns the previous state makes React call the component
      // again, which renders every row; skipping the call renders nothing.
      if (!haveSameOffsets(offsetsRef.current, nextOffsets)) {
        offsetsRef.current = nextOffsets;
        setOffsets(nextOffsets);
      }
    };

    measure();
    const observer = new ResizeObserver(measure);
    [...startFields, ...endFields].forEach((field) => {
      const cell = headerCells.get(field);

      if (cell) {
        observer.observe(cell);
      }
    });

    return () => observer.disconnect();
  }, [endFields, leadingColumnsWidth, startFields, tableRef]);

  return offsets;
}
